import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCheckCircle,
  faCreditCard,
  faLocationDot,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import Postnav from '../../components/Postnav/Postnav';
import Footer from '../../components/Footer/Footer';
import Loader from '../../components/Loader/Loader';
import './Precompra.css';

function Precompra() {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [compraCreada, setCompraCreada] = useState(null);

  const formatoPrecio = (valor) => new Intl.NumberFormat('es-AR', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
    style: 'currency',
    currency: 'ARS',
  }).format(Number(valor || 0));

  const totales = useMemo(() => productos.reduce((acc, producto) => {
    const cantidad = Number(producto.cant || 0);
    const precio = Number(producto.precio || 0);
    return {
      cantidad: acc.cantidad + cantidad,
      precio: acc.precio + (precio * cantidad),
    };
  }, { cantidad: 0, precio: 0 }), [productos]);

  useEffect(() => {
    const cargarCarrito = async () => {
      try {
        setLoading(true);
        setErrorMessage('');
        const response = await fetch('/api/carrito', {
          method: 'GET',
          credentials: 'include',
        });

        if (response.status === 401) {
          window.location.href = '/login';
          return;
        }

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || 'No pudimos cargar el carrito.');
        }

        setProductos(Array.isArray(data.avisos) ? data.avisos : []);
      } catch (error) {
        console.error('Error al cargar precompra:', error);
        setErrorMessage(error.message);
      } finally {
        setLoading(false);
      }
    };

    cargarCarrito();
  }, []);

  const handleComprar = async () => {
    try {
      setCheckoutLoading(true);
      setErrorMessage('');

      const response = await fetch('/api/checkout', {
        method: 'POST',
        credentials: 'include',
      });

      if (response.status === 401) {
        window.location.href = '/login';
        return;
      }

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'No pudimos crear la compra.');
      }

      setCompraCreada(data.compra);
      setProductos([]);
    } catch (error) {
      console.error('Error al confirmar compra:', error);
      setErrorMessage(error.message);
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <div>
      <div className="outheader">
        <div className="background">
          <div className="background-center-color">
            <div className="postnav-container">
              <Postnav />
            </div>

            <main className="checkout-page">
              <section className="checkout-heading">
                <p>Confirmacion</p>
                <h1>Finalizar compra</h1>
              </section>

              {loading ? (
                <section className="checkout-state">
                  <Loader />
                  <p>Cargando tu compra</p>
                </section>
              ) : compraCreada ? (
                <section className="checkout-success">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <h2>Compra creada</h2>
                  <p>Tu pedido quedo registrado como pendiente de pago.</p>
                  <div className="checkout-success-summary">
                    <span>Compra</span>
                    <strong>#{compraCreada._id.slice(-8).toUpperCase()}</strong>
                    <span>Total</span>
                    <strong>{formatoPrecio(compraCreada.total_precio)}</strong>
                  </div>
                  <Link to="/cuenta#MisCompras">Ver mis compras</Link>
                </section>
              ) : productos.length > 0 ? (
                <section className="checkout-layout">
                  <div className="checkout-products">
                    <div className="checkout-card-heading">
                      <h2>Productos</h2>
                      <Link to="/tucarrito">Editar carrito</Link>
                    </div>

                    {productos.map((producto, index) => {
                      const avisoid = producto.avisoid;
                      const titulo = producto.titulo || `Producto #${avisoid}`;
                      const cantidad = Number(producto.cant || 0);
                      const precio = Number(producto.precio || 0);

                      return (
                        <article className="checkout-item" key={`${avisoid}-${index}`}>
                          <Link to={`/aviso/${avisoid}`} className="checkout-item-media" aria-label={titulo}>
                            {producto.imagen ? <img src={producto.imagen} alt={titulo} /> : <span>{avisoid}</span>}
                          </Link>
                          <div>
                            <Link to={`/aviso/${avisoid}`} className="checkout-item-title">{titulo}</Link>
                            <p>{cantidad} unidad{cantidad === 1 ? '' : 'es'} x {formatoPrecio(precio)}</p>
                          </div>
                          <strong>{formatoPrecio(precio * cantidad)}</strong>
                        </article>
                      );
                    })}
                  </div>

                  <aside className="checkout-summary">
                    <h2>Resumen</h2>
                    <div className="checkout-summary-row">
                      <span>Productos</span>
                      <strong>{totales.cantidad}</strong>
                    </div>
                    <div className="checkout-summary-row">
                      <span>Subtotal</span>
                      <strong>{formatoPrecio(totales.precio)}</strong>
                    </div>
                    <div className="checkout-info-row">
                      <FontAwesomeIcon icon={faLocationDot} />
                      <span>Entrega a coordinar con la tienda.</span>
                    </div>
                    <div className="checkout-info-row">
                      <FontAwesomeIcon icon={faShieldHalved} />
                      <span>El pedido queda asociado a tu cuenta.</span>
                    </div>
                    <div className="checkout-total">
                      <span>Total</span>
                      <strong>{formatoPrecio(totales.precio)}</strong>
                    </div>
                    {errorMessage && <p className="checkout-message error">{errorMessage}</p>}
                    <button type="button" onClick={handleComprar} disabled={checkoutLoading}>
                      <FontAwesomeIcon icon={faCreditCard} />
                      {checkoutLoading ? 'Creando compra...' : 'Confirmar compra'}
                    </button>
                  </aside>
                </section>
              ) : (
                <section className="checkout-empty">
                  <h2>No hay productos para comprar</h2>
                  <p>Agrega productos al carrito para poder finalizar una compra.</p>
                  <Link to="/productos">Ver catalogo <FontAwesomeIcon icon={faArrowRight} /></Link>
                </section>
              )}
            </main>

            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Precompra;
